/**
 * Tour P5 (2026-07-07) — saveData del wizard "Nuevo recorrido" (patrón
 * pass-data/save-data de la app: los procesos fuera de modales se ejecutan
 * en la `action` del router, no en hooks del componente — mismo esquema que
 * `workOrders/NewOrder/WorkOrderSummary/ActionUtils/saveData` y que
 * `NewSurveillanceRouteOrder/Summary/action.ts`).
 *
 * Recibe por formData la Ubicación (el yup del componente ya la validó) y la
 * tripulación YA DERIVADA y validada (`deriveCrew` en el componente, la misma
 * derivación que alimenta las frases FR-3.4 en vivo).
 * Crea el RV vía SAVE_SURVEILLANCE_ROUTE (el backend crea la Order doble
 * y los VehicleOccupant en una única $transaction) y devuelve el
 * `redirect(...)` al detalle.
 */

import { redirect } from 'react-router-dom';

import { client } from '../../../../gql/client';
import { SAVE_SURVEILLANCE_ROUTE } from '../../../../gql/mutations/surveillanceRoute';
import { prependSurveillanceRouteToCache } from '../../../../gql/cacheHelpers';
import { formNaming as formSurveillanceRouteNaming } from '../../../../components/forms/SurveillanceRouteForm/constants';
import { parsePk } from '../../../../utils/transform';
import { showSuccessToast } from '../../../../utils/toasts';
import { removeNewSurveillanceRouteStorage } from '../../../../utils/resetData';
import i18n from '../../../../i18n';
import { isActuallyOnline } from '../../../../offline/checkConnectivity';
import { enqueueMutation } from '../../../../offline/offlineQueue';
import { ownerForEnqueue } from '../../../../offline/queueOwner';
import { createOfflineQueuedError } from '../../../../offline/isOfflineQueuedError';

/** Clave sessionStorage del borrador de la tab Operarios (la persiste
 *  useCacheFormInSessionStorage dentro de OperatorsAndVehicleForm).
 *  Definida en el naming del form RV para que el logout la purgue
 *  (BUG borrador RV 2026-07-12). */
export const SR_OPERATORS_FORM_KEY =
  formSurveillanceRouteNaming.NEW_SURVEILLANCE_ROUTE_OPERATORS_FORM;

/**
 * `clientOpId` ESTABLE mientras dure este asistente.
 *
 * Se generaba con `crypto.randomUUID()` en cada llamada, así que la
 * idempotencia del backend no servía para nada en el caso que más importa: el
 * usuario pulsa Iniciar, la respuesta no llega (timeout, red de campo), vuelve
 * a pulsar → clave nueva → **dos recorridos**. Guardándola junto al borrador,
 * el reintento reusa la misma clave y el backend devuelve el recorrido que ya
 * creó. La purga al entrar y la de después de crear con éxito la retiran, así
 * que cada asistente es una operación lógica distinta.
 */
const resolveClientOpId = (): string => {
  const key = `${SR_OPERATORS_FORM_KEY}_opid`;
  const stored = sessionStorage.getItem(key);
  if (stored) return stored;
  const fresh = crypto.randomUUID();
  sessionStorage.setItem(key, fresh);
  return fresh;
};

export const saveData = async (formData: FormData): Promise<Response> => {
  const name = String(formData.get('name') ?? '').trim();
  // D2 (2026-07-20): descripción real del formulario (OPCIONAL) — antes se
  // mandaba `''` fijo y el backend sembraba `Order.description = name`
  // (Addendum 2026-07-20 (2)). El backend YA no hace ese fallback (D1).
  const description = String(formData.get('description') ?? '').trim();
  const concessionId = String(formData.get('concessionId') ?? '');
  // Multi-carretera (2026-08-15): la lista de carreteras y el rango de cada
  // una. Los `pkInit`/`pkEnd` sueltos son los de la PRIMERA, que es lo que el
  // backend guarda en el propio recorrido.
  const roadIds = JSON.parse(
    String(formData.get('roadIds') ?? '[]'),
  ) as string[];
  const roadRangeRows = JSON.parse(
    String(formData.get('roadRanges') ?? '[]'),
  ) as Array<{ roadId: string; pkInit: string; pkEnd: string }>;
  const teamId = String(formData.get('teamId') ?? '') || undefined;
  const pkInitParsed = parsePk(String(formData.get('pkInit') ?? ''));
  const pkEndParsed = parsePk(String(formData.get('pkEnd') ?? ''));

  // Guard defensivo: el yup del componente ya validó todo esto, así que si
  // falta algo hay una inconsistencia real → throw (el catch de action.ts
  // captura a Sentry y toastea el error genérico).
  if (
    !name ||
    !concessionId ||
    roadIds.length === 0 ||
    !pkInitParsed ||
    !pkEndParsed
  ) {
    throw new Error('[NewRoute saveData] campos requeridos ausentes tras validación yup');
  }

  // Con UNA sola carretera no se mandan rangos: los PK del recorrido ya son los
  // suyos, y así el alta queda idéntica a antes de multi-carretera.
  const roadRanges =
    roadIds.length > 1
      ? roadRangeRows.flatMap((row) => {
          const init = parsePk(row.pkInit);
          const end = parsePk(row.pkEnd);
          if (!init || !end) return [];
          return [
            {
              roadId: row.roadId,
              pkInitKm: init.km,
              pkInitMeter: init.meter,
              pkEndKm: end.km,
              pkEndMeter: end.meter,
            },
          ];
        })
      : undefined;

  // 🔴 Tripulación: llega YA DERIVADA y validada por el componente (incidente
  // 2026-08-08). Antes se releía aquí el borrador crudo de sessionStorage, así
  // que se validaba una cosa y se creaba otra: los restos de un intento
  // abandonado —invisibles si el filtro de equipo ocultaba sus filas— nacían
  // como operarios y vehículos REALES del recorrido. Ahora el payload solo
  // puede contener lo que pasó por las validaciones FR-3.4 de la pantalla.
  const rawCrew = String(formData.get('crew') ?? '');
  if (!rawCrew) {
    throw new Error('[NewRoute saveData] falta la tripulación derivada en el formData');
  }
  const crew = JSON.parse(rawCrew) as {
    operatorIds: string[];
    assignments: { vehicleId: string; driverId: string }[];
  };

  const input = {
        name,
        description,
        concessionId,
        roadIds,
        roadRanges,
        pkInitKm: pkInitParsed.km,
        pkInitMeter: pkInitParsed.meter,
        pkEndKm: pkEndParsed.km,
        pkEndMeter: pkEndParsed.meter,
        teamIds: teamId ? [teamId] : undefined,
        // V4 post-refactor 2026-04-22: `operatorIDs` → `userIDs` (patrón Action).
        userIDs: crew.operatorIds,
        // Parejas vehículo→conductor — el backend crea los VehicleOccupant
        // dentro de su $transaction.
        vehicleAssignments: crew.assignments,
        // Hora de creación capturada en el CLIENTE (offline-safe, par de
        // rois-back#73): sin esto, un RV creado sin red se reproduce al
        // reconectar y el backend estampaba creationDate/registerInitDate con
        // su now() = hora de sync. El backend usa `input.registerInitDate ?? now`.
        registerInitDate: new Date().toISOString(),
        // Idempotencia (espejo ft/poison-pill): clientOpId estable por
        // operación lógica — el replay offline / reintento de red devuelve
        // el RV ya creado sin duplicar RV + Order.
        clientOpId: resolveClientOpId(),
  };

  // Sondeo previo (paridad con Actuación/Accidente, 2026-09-08): con una raya
  // de cobertura `navigator.onLine` dice «online» y la mutation se colgaría
  // hasta el techo del link. Si ningún backend contesta al /health en 2,5 s,
  // se encola directamente por el MISMO camino que el link (misma
  // serialización, mismo centinela) y el catch de action.ts no distingue.
  // El dueño es el de quien PULSA, no el de quien haya cuando acabe el
  // sondeo (spec 2026-10-01 § 12.2).
  const owner = ownerForEnqueue();
  if (!(await isActuallyOnline())) {
    await enqueueMutation(SAVE_SURVEILLANCE_ROUTE, { input }, owner);
    throw createOfflineQueuedError();
  }

  const result = await client.mutate({
    mutation: SAVE_SURVEILLANCE_ROUTE,
    variables: { input },
  });

  const route = result.data?.saveSurveillanceRoute?.surveillanceRoute;
  if (!route?.id) {
    throw new Error('[NewRoute saveData] saveSurveillanceRoute sin id en la respuesta');
  }

  // Añadir el nuevo RV a la lista cacheada (BUG lista RV stale 2026-07-31): el
  // loader del listado es cache-read-first y NO refetchea si la caché ya tiene
  // la lista → sin esto, solo el 1er RV tras login aparecía (caché fría) y los
  // siguientes no salían al volver a la tabla. Espejo del resto de Save<Entity>
  // (prependGenericOrderToCache…). El create OFFLINE lo cubre
  // `applyOperationCacheUpdate` al reproducir la cola.
  prependSurveillanceRouteToCache(client.cache, route);

  // Y al CRONÓMETRO, que es otra lista (2026-08-19).
  //
  // El overlay del cronómetro no lee `surveillanceRoutes` sino
  // `activeSurveillanceRoutes` —otro campo, con sus propios argumentos de
  // concesión y equipo—, así que el prepend de arriba no le llega. Su consulta
  // es `cache-and-network` con un sondeo de 30 s: sin esto, el recorrido recién
  // creado tardaba hasta MEDIO MINUTO en aparecer en el desplegable, que es
  // justo cuando el operario va a iniciar su primer tramo.
  //
  // Se desaloja el campo en vez de insertar a mano: la lista viene filtrada por
  // concesión y equipo, y meter una fila en un cajón sin saber si cumple el
  // filtro es peor que pedirla otra vez. Al quedar sin caché, el `useQuery` vivo
  // del overlay va a red al momento. Mismo gesto que ya hace `RelateSegmentModal`
  // con este mismo campo.
  //
  // Offline no se llega aquí: el `client.mutate` de arriba rechaza con el error
  // sintético de la cola y esta función sale antes, así que desalojar no puede
  // dejar el cronómetro en blanco sin red.
  client.cache.evict({ fieldName: 'activeSurveillanceRoutes' });
  client.cache.gc();

  // Borrador consumido: limpiar la key para que el siguiente "Nuevo
  // recorrido" arranque vacío (patrón del wizard de actuaciones, que limpia
  // formActionNaming.* tras guardar).
  // BUG borrador RV (2026-07-12): también las cachés hermanas del form — el
  // bucket de conductores por-wizard (`_drivers`) y los buckets
  // _interno/_externals que persiste el propio form al cambiar de pestaña.
  // Sin esto, los conductores del RV recién creado resucitaban como
  // vehículos pre-marcados en el siguiente wizard. Quién es "todas las keys"
  // lo sabe un solo sitio (`utils/resetData.ts`).
  removeNewSurveillanceRouteStorage('new');
  showSuccessToast(i18n.t('toasts:success.created'));

  return redirect(`/surveillance-route/${route.id}`);
};
